import {
  createElement,
  createHook,
  forwardRef,
  memo,
} from "@ariakit/react-utils";
import type { Props } from "@ariakit/react-utils";
import type { ElementType } from "react";
import { withDefaultButtonType } from "../button/utils.ts";
import type { CompositeItemOptions } from "../composite/composite-item.tsx";
import { useCompositeItem } from "../composite/composite-item.tsx";
import { useToolbarContext } from "./toolbar-context.tsx";
import type { ToolbarStore } from "./toolbar-store.ts";

const TagName = "button" satisfies ElementType;
type TagName = typeof TagName;

/**
 * Returns props to create a `ToolbarItem` component.
 * @see https://ariakit.com/components/toolbar
 * @example
 * ```jsx
 * const store = useToolbarStore();
 * const props = useToolbarItem({ store });
 * <Toolbar store={store}>
 *   <Role {...props}>Item</Role>
 * </Toolbar>
 * ```
 */
export const useToolbarItem = createHook<TagName, ToolbarItemOptions>(
  function useToolbarItem({ store, ...props }) {
    const context = useToolbarContext();
    store = store || context;
    props = useCompositeItem({ store, ...props });
    return props;
  },
);

/**
 * Renders an interactive element inside a
 * [`Toolbar`](https://ariakit.com/reference/toolbar).
 * @see https://ariakit.com/components/toolbar
 * @example
 * ```jsx {2}
 * <Toolbar>
 *   <ToolbarItem>Item</ToolbarItem>
 * </Toolbar>
 * ```
 */
export const ToolbarItem = memo(
  forwardRef(function ToolbarItem(props: ToolbarItemProps) {
    const htmlProps = useToolbarItem(withDefaultButtonType(props));
    return createElement(TagName, htmlProps);
  }),
);

export interface ToolbarItemOptions<
  T extends ElementType = TagName,
> extends CompositeItemOptions<T> {
  /**
   * Object returned by the
   * [`useToolbarStore`](https://ariakit.com/reference/use-toolbar-store) hook.
   * If not provided, the closest
   * [`Toolbar`](https://ariakit.com/reference/toolbar) component's context will
   * be used.
   */
  store?: ToolbarStore;
}

export type ToolbarItemProps<T extends ElementType = TagName> = Props<
  T,
  ToolbarItemOptions<T>
>;
